import { Suspense, lazy, useState } from 'react';
import { getEncryptedLocalStorageItem, setEncryptedLocalStorageItem } from './utils/localStorage';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import Loader from './SharedComponents/loadingComponent';
import { PrivateLoginRoute, PrivateRoute } from './utils/authUtil';

const LoginComponent = lazy(() => import('./components/LoginComponent'));
const DashboardComponent = lazy(() => import('./components/DashboardComponent'));
const StatusCodeComponent = lazy(() => import('./components/StatusCodeComponent'));
const DeviceImageComponent = lazy(() => import('./components/DeviceImageComponent'));
const DeviceTypesComponent = lazy(() => import('./components/DeviceTypesComponent'));
const CommunicationChannelComponent = lazy(() => import('./components/CommunicationChannelComponent'));
const DeviceConfigComponent = lazy(() => import('./components/DeviceConfigComponent'));
const DeviceSelfConfigComponent = lazy(() => import('./components/DeviceSelfConfigComponent'));
const DeviceHistoryComponent = lazy(() => import('./components/DeviceHistoryComponent'));
const DeviceLogsComponent = lazy(() => import('./components/DeviceLogsComponent'));
const DeviceGraphComponent = lazy(() => import('./components/DeviceGraphComponent'));
const PendingRequestComponent = lazy(() => import('./components/PendingRequestComponent'));
const HMIDeviceCardComponent = lazy(() => import('./components/HMIDeviceCardComponent'));
const NotificationEngineComponent = lazy(() => import('./components/NotificationEngineComponent'));
// const NotificationLogsComponent = lazy(() => import('./components/NotificationLogsComponent'));
const LocationsComponents = lazy(() => import('./components/LocationsComponents'));
const FrequencyComponent = lazy(() => import('./components/FrequencyComponent'));
const CreatorHMIComponent = lazy(() => import('./components/CreatorHMIComponent'));
const TimeGroupComponent = lazy(() => import('./components/TimeGroupComponent'));

const ColorPaletteComponent = lazy(() => import('./components/ColorPaletteComponent'));
const BaudRateComponent = lazy(() => import('./components/BaudRateComponent'));
const ByteOrderComponent = lazy(() => import('./components/ByteOrderComponent'));
const DataBitComponent = lazy(() => import('./components/DataBitComponent'));
const DataTypeComponent = lazy(() => import('./components/DataTypeComponent'));
const ModbusCommandTypeComponent = lazy(() => import('./components/ModbusCommandTypeComponent'));
const ModbusTypeComponent = lazy(() => import('./components/ModbusTypeComponent'));

const DeviceUserLinkingComponent = lazy(() => import('./components/DeviceUserLinkingComponent'));
const DeviceTagLinkingComponent = lazy(() => import('./components/DeviceTagLinkingComponent'));
const EFMUserComponent = lazy(() => import('./components/EFMUserComponent'));

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
  },
});


const lightTheme = createTheme({
  palette: {
    mode: 'light',
  },
});

function App() {
  const getCurrentTheme = getEncryptedLocalStorageItem("dark-mode") != null
    ? getEncryptedLocalStorageItem("dark-mode")
    : window.matchMedia("(prefers-color-scheme: dark)").matches;

  // Initialize the theme state with dark theme as the default
  const [currentTheme, setCurrentTheme] = useState(getCurrentTheme ? darkTheme : lightTheme);

  // Function to toggle between dark and light themes
  const toggleTheme = () => {
    setCurrentTheme(currentTheme === darkTheme ? lightTheme : darkTheme);
    setEncryptedLocalStorageItem("dark-mode", currentTheme === darkTheme ? false : true);
  }

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <BrowserRouter basename="/">
        <ToastContainer limit={1} />

        <Suspense fallback={<Loader />}>
          <Routes>
            <Route
              exact
              path="/login"
              element={
                <PrivateLoginRoute element={<LoginComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Login"
                />} />
              }
            />
            <Route
              exact
              path="/dashboard"
              element={
                <PrivateRoute currentTheme={currentTheme} title="Dashboard" toggleTheme={toggleTheme} element={<DashboardComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Dashboard"
                />} />
              }
            />

            <Route
              exact
              path="/creator-hmi"
              element={
                <PrivateRoute currentTheme={currentTheme} title="Creator HMI" toggleTheme={toggleTheme} element={<CreatorHMIComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Creator HMI"
                />} />
              }
            />

            <Route
              exact
              path="/status-code"
              element={
                <PrivateRoute currentTheme={currentTheme} title="Status Codes" toggleTheme={toggleTheme} element={<StatusCodeComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Status Codes"
                />} />
              }
            />

            <Route
              exact
              path="/device-image"
              element={
                <PrivateRoute currentTheme={currentTheme} title="Device Image" toggleTheme={toggleTheme} element={<DeviceImageComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Device Image"
                />} />
              }
            />

            <Route
              exact
              path="/device-types"
              element={
                <PrivateRoute currentTheme={currentTheme} title="Device Types" toggleTheme={toggleTheme} element={<DeviceTypesComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Device Types"
                />} />
              }
            />

            <Route
              exact
              path="/color-palette"
              element={
                <PrivateRoute currentTheme={currentTheme} title="Color Palette" toggleTheme={toggleTheme} element={<ColorPaletteComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Color Palette"
                />} />
              }
            />

            <Route
              exact
              path="/communication-channel"
              element={
                <PrivateRoute currentTheme={currentTheme} title="Communication Channel" toggleTheme={toggleTheme} element={<CommunicationChannelComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Communication Channel"
                />} />
              }
            />
            <Route
              exact
              path="/device-config"
              element={
                <PrivateRoute currentTheme={currentTheme} title="Device Configuration" toggleTheme={toggleTheme} element={<DeviceConfigComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Device Configuration"
                />} />
              }
            />

            <Route
              exact
              path="/device-self-config"
              element={
                <PrivateRoute currentTheme={currentTheme} title="Device Self Configuration" toggleTheme={toggleTheme} element={<DeviceSelfConfigComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Device Self Configuration"
                />} />
              }
            />

            <Route
              exact
              path="/device-history"
              element={
                <PrivateRoute currentTheme={currentTheme} title="Device History" toggleTheme={toggleTheme} element={<DeviceHistoryComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Device History"
                />} />
              }
            />

            <Route
              exact
              path="/device-logs"
              element={
                <PrivateRoute currentTheme={currentTheme} title="Device Logs" toggleTheme={toggleTheme} element={<DeviceLogsComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Device Logs"
                />} />
              }
            />

            <Route
              exact
              path="/device-pending-request"
              element={
                <PrivateRoute currentTheme={currentTheme} title="Device Pending Request" toggleTheme={toggleTheme} element={<PendingRequestComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Device Pending Request"
                />} />
              }
            />

            <Route
              exact
              path="/device-graph"
              element={
                <PrivateRoute currentTheme={currentTheme} title="Device Graph" toggleTheme={toggleTheme} element={<DeviceGraphComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Device Graph"
                />} />
              }
            />

            <Route
              exact
              path="/hmi-device-card"
              element={
                <PrivateRoute currentTheme={currentTheme} title="HMI Device Card" toggleTheme={toggleTheme} element={<HMIDeviceCardComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="HMI Device Card"
                />} />
              }
            />

            <Route
              exact
              path="/location"
              element={
                <PrivateRoute currentTheme={currentTheme} title="Locations" toggleTheme={toggleTheme} element={<LocationsComponents
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Locations"
                />} />
              }
            />

            <Route
              exact
              path="/frequency"
              element={
                <PrivateRoute currentTheme={currentTheme} title="Frequency" toggleTheme={toggleTheme} element={<FrequencyComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Frequency"
                />} />
              }
            />

            <Route
              exact
              path="/notification-engine"
              element={
                <PrivateRoute currentTheme={currentTheme} title="Notification Engine" toggleTheme={toggleTheme} element={<NotificationEngineComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Notification Engine"
                />} />
              }
            />

            {/* <Route
              exact
              path="/notification-logs"
              element={
                <PrivateRoute currentTheme={currentTheme} title="Notification Logs" toggleTheme={toggleTheme} element={<NotificationLogsComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Notification Logs"
                />} />
              }
            /> */}



            <Route
              exact
              path="/time-group"
              element={
                <PrivateRoute currentTheme={currentTheme} title="Time Group" toggleTheme={toggleTheme} element={<TimeGroupComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Time Group"
                />} />
              }
            />

            <Route
              exact
              path="/baud-rate"
              element={
                <PrivateRoute currentTheme={currentTheme} title="Baud Rate" toggleTheme={toggleTheme} element={<BaudRateComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Baud Rate"
                />} />
              }
            />

            <Route
              exact
              path="/byte-order"
              element={
                <PrivateRoute currentTheme={currentTheme} title="Byte Order" toggleTheme={toggleTheme} element={<ByteOrderComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Byte Order"
                />} />
              }
            />

            <Route
              exact
              path="/data-bit"
              element={
                <PrivateRoute currentTheme={currentTheme} title="Data Bit" toggleTheme={toggleTheme} element={<DataBitComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Data Bit"
                />} />
              }
            />

            <Route
              exact
              path="/data-type"
              element={
                <PrivateRoute currentTheme={currentTheme} title="Data Type" toggleTheme={toggleTheme} element={<DataTypeComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Data Type"
                />} />
              }
            />

            <Route
              exact
              path="/modbus-command-type"
              element={
                <PrivateRoute currentTheme={currentTheme} title="Modbus Command Type" toggleTheme={toggleTheme} element={<ModbusCommandTypeComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Modbus Command Type"
                />} />
              }
            />

            <Route
              exact
              path="/modbus-type"
              element={
                <PrivateRoute currentTheme={currentTheme} title="Modbus Type" toggleTheme={toggleTheme} element={<ModbusTypeComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Modbus Type"
                />} />
              }
            />

            <Route
              exact
              path="/device-user-linking"
              element={
                <PrivateRoute currentTheme={currentTheme} title="Device User Linking" toggleTheme={toggleTheme} element={<DeviceUserLinkingComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Device User Linking"
                />} />
              }
            />

            <Route
              exact
              path="/device-tag-linking"
              element={
                <PrivateRoute currentTheme={currentTheme} title="Device Tag Linking" toggleTheme={toggleTheme} element={<DeviceTagLinkingComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="Device Tag Linking"
                />} />
              }
            />

            <Route
              exact
              path="/efm-user"
              element={
                <PrivateRoute currentTheme={currentTheme} title="EFM User" toggleTheme={toggleTheme} element={<EFMUserComponent
                  currentTheme={currentTheme === darkTheme} toggleTheme={toggleTheme}
                  title="EFM User"
                />} />
              }
            />


            <Route path="*" element={<Navigate to={{ pathname: "/dashboard" }} />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
